import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
 User,
 Building,
 Pencil,
 ArrowRight,
 MapPin,
 Image as ImageIcon,
 FileText,
 ListChecks,
} from 'lucide-react';
import { ToastContainer, useToast } from '../../components/shared/Toast';
import {
 ProfileInformationTab,
 BusinessProfileTab,
} from '../../components/vendor/profile';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorProfile = () => {
 const { user, updateUser } = useAuth();
 const toast = useToast();
 const [activeTab, setActiveTab] = useState('profile');

 const tabs = [
 { id: 'profile', name: 'Profile', icon: User },
 { id: 'business', name: 'Business', icon: Building },
 ];

 const editHref =
 activeTab === 'business' ? '/vendor/account?tab=business' : '/vendor/account?tab=profile';

 const quickLinkClass =
 'group rounded-xl border border-border bg-surface p-4 hover:border-border hover:shadow-sm transition-all';

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Profile"
 subtitle="Preview how your profile and business details appear. Editing happens on the Account page."
 actions={
 <Link
 to={editHref}
 className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-ink px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-black transition-colors"
 >
 <Pencil className="w-4 h-4" />
 Edit {activeTab === 'business' ? 'business details' : 'account'}
 </Link>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.profile.title}
 steps={VENDOR_PAGE_GUIDES.profile.steps}
 icon={ListChecks}
 action={
 <div className="flex flex-wrap gap-2">
 <Link
 to="/vendor/account?tab=profile"
 className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs sm:text-sm font-medium text-ink hover:bg-surface-muted transition-colors"
 >
 <User className="w-3.5 h-3.5" />
 Edit profile info
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 <Link
 to="/vendor/account?tab=business"
 className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs sm:text-sm font-medium text-ink hover:bg-surface-muted transition-colors"
 >
 <Building className="w-3.5 h-3.5" />
 Edit business &amp; logo
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 <Link
 to="/vendor/account?tab=terms"
 className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs sm:text-sm font-medium text-ink hover:bg-surface-muted transition-colors"
 >
 <FileText className="w-3.5 h-3.5" />
 Edit terms
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 </div>
 }
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <Link to="/vendor/account?tab=business" className={quickLinkClass}>
 <div className="flex items-center gap-2 text-sm font-semibold text-ink">
 <ImageIcon className="w-4 h-4 text-ink" />
 Business logo
 </div>
 <p className="mt-1.5 text-xs text-ink-secondary leading-relaxed">
 Upload the logo used on invoices, receipts, and contracts.
 </p>
 <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink group-hover:underline">
 Update logo <ArrowRight className="w-3 h-3" />
 </span>
 </Link>
 <Link to="/vendor/account?tab=business" className={quickLinkClass}>
 <div className="flex items-center gap-2 text-sm font-semibold text-ink">
 <MapPin className="w-4 h-4 text-ink" />
 Location &amp; address
 </div>
 <p className="mt-1.5 text-xs text-ink-secondary leading-relaxed">
 Keep your business address and operating location accurate.
 </p>
 <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink group-hover:underline">
 Update location <ArrowRight className="w-3 h-3" />
 </span>
 </Link>
 <Link to="/vendor/account?tab=profile" className={quickLinkClass}>
 <div className="flex items-center gap-2 text-sm font-semibold text-ink">
 <User className="w-4 h-4 text-ink" />
 Personal profile
 </div>
 <p className="mt-1.5 text-xs text-ink-secondary leading-relaxed">
 Change your display name, contact details, and profile photo.
 </p>
 <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink group-hover:underline">
 Update profile <ArrowRight className="w-3 h-3" />
 </span>
 </Link>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5 overflow-x-auto">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border min-w-max"
 role="tablist"
 aria-label="Profile preview"
 >
 {tabs.map((tab) => {
 const Icon = tab.icon;
 const isActive = activeTab === tab.id;
 return (
 <button
 key={tab.id}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setActiveTab(tab.id)}
 className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 <Icon className="w-4 h-4 flex-shrink-0" />
 <span>{tab.name}</span>
 </button>
 );
 })}
 </div>
 </div>

 <div className="p-4 sm:p-6 lg:p-8">
 <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <p className="text-xs sm:text-sm text-ink-tertiary">
 Showing a read-only preview.
 {activeTab === 'business'
 ? ' Edit business name, address, and logo in Account.'
 : ' Edit personal details in Account.'}
 </p>
 <Link
 to={editHref}
 className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:text-ink"
 >
 <Pencil className="w-3.5 h-3.5" />
 Edit on Account page
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 </div>
 {activeTab === 'profile' && (
 <ProfileInformationTab user={user} updateUser={updateUser} readOnly={true} />
 )}
 {activeTab === 'business' && (
 <BusinessProfileTab user={user} updateUser={updateUser} readOnly={true} />
 )}
 </div>
 </div>

 <ToastContainer toasts={toast.toasts} position="top-right" onRemove={toast.removeToast} />
 </div>
 );
};

export default VendorProfile;
